import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonLoading,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Stack,
  Button,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  DateRange,
  Search,
  Refresh,
  Download,
  FilterList,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  AccountBalance,
  CalendarMonth,
  Info,
  ViewList,
  ViewDay,
  Print as PrintIcon,
  Warning,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { requestID, baseUrl, config } from "../../common/utils";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useBusinessContext } from "../../context/business-context";
import ReactToPrint from "react-to-print";
import { useReactToPrint } from 'react-to-print';
import { MenuButton } from "../../components/Menu";

const LedgerReport_sales_only_admin_view = () => {
  const theme = useTheme();
  const componentRef = useRef();
  const printContentRef = useRef();
  
  const schema = yup
    .object({
      ledger: yup.string().required("Please select a ledger"),
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ledger: "",
    },
  });

  const { name } = useParams<{ name: string }>();
  const { selectedBusiness } = useBusinessContext();

  const [status, setStatus] = useState({ loading: false, error: false });
  const [errorLogin, setErrorLogin] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [posting, setPosting] = useState([]);
  const [allLedger, setAllLedger] = useState([]);
  const [summary, setSummary] = useState({
    totalExpenses: 0,
    totalProfit: 0,
    totalLoss: 0,
    netBalance: 0,
    transactionCount: 0,
  });

  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [runningBalance, setRunningBalance] = useState(0);
  const [viewMode, setViewMode] = useState('detailed');
  const [dailySummary, setDailySummary] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  
  // New states for delete functionality
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState({ loading: false, error: false });

  const getLedgers = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());
    
    setStatus({ loading: true, error: false });
    axios
      .post(baseUrl() + "ledgercreates/getAllLedger_caas_branch_sales_only", formData, config)
      .then((response) => {
        setAllLedger(response.data.data || []);
        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        console.error("Error fetching ledgers:", error);
        setStatus({ loading: false, error: false });
      });
  };

  useIonViewWillEnter(() => {
    getLedgers();
  });

  useEffect(() => {
    getLedgers();
  }, []);

  // Helper function to extract loss amount from itemCode
  const extractLossAmount = (itemCode) => {
    if (!itemCode) return 0;
    
    // Try to parse the itemCode as a number
    const parsed = parseFloat(itemCode);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Function to group transactions by day
  const groupTransactionsByDay = (transactions) => {
    const grouped = {};
    
    transactions.forEach((transaction) => {
      const date = dayjs(transaction.dateCreated).format('YYYY-MM-DD');
      const formattedDate = dayjs(transaction.dateCreated).format('DD MMM YYYY');
      const dayOfWeek = dayjs(transaction.dateCreated).format('dddd');
      
      if (!grouped[date]) {
        grouped[date] = {
          date: date,
          formattedDate: formattedDate,
          dayOfWeek: dayOfWeek,
          totalExpenses: 0,
          totalProfit: 0,
          totalLoss: 0,
          transactionCount: 0,
          transactions: [],
          netBalance: 0,
          runningBalance: 0
        };
      }
      
      const expense = parseFloat(transaction.dr) || 0;
      const profit = parseFloat(transaction.cr) || 0;
      // Extract loss from itemCode if progID is LOSS
      const loss = transaction.progID === 'LOSS' ? extractLossAmount(transaction.itemCode) : 0;
      
      grouped[date].totalExpenses += expense;
      grouped[date].totalProfit += profit;
      grouped[date].totalLoss += loss;
      grouped[date].transactionCount += 1;
      grouped[date].transactions.push(transaction);
      grouped[date].netBalance = grouped[date].totalProfit - grouped[date].totalExpenses - grouped[date].totalLoss;
    });
    
    // Convert to array and sort by date
    const result = Object.values(grouped).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // Calculate running balance for daily summary
    let cumulativeBalance = 0;
    result.forEach((day) => {
      cumulativeBalance += day.netBalance;
      day.runningBalance = cumulativeBalance;
    });
    
    return result;
  };

  const getPostingReport = async (data) => {
    if (!data.ledger) {
      setErrorLogin("Please select a ledger first");
      setShowToast(true);
      return;
    }
    
    setStatus({ loading: true, error: false });
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("startDate", startDate.format('YYYY-MM-DD'));
      formData.append("endDate", endDate.format('YYYY-MM-DD'));
      formData.append("sn", data.ledger);
      
      const response = await axios.post(
        baseUrl() + "ledgers/getPostingReport_sales_only_xchange",
        formData,
        config
      );
      
      const dataResponse = response.data.data || [];
      
      // Sort by date ascending for proper running balance calculation
      const sortedData = [...dataResponse].sort((a, b) => 
        new Date(a.dateCreated) - new Date(b.dateCreated)
      );
      
      setPosting(sortedData);
      
      // Calculate daily summary
      const dailyGrouped = groupTransactionsByDay(sortedData);
      setDailySummary(dailyGrouped);
      
      // Calculate summary for four columns
      const totalExpenses = sortedData.reduce((sum, item) => sum + (parseFloat(item.dr) || 0), 0);
      const totalProfit = sortedData.reduce((sum, item) => sum + (parseFloat(item.cr) || 0), 0);
      const totalLoss = sortedData.reduce((sum, item) => {
        // Check if transaction is a loss (progID == 'LOSS')
        // If yes, extract loss amount from itemCode
        if (item.progID === 'LOSS') {
          return sum + extractLossAmount(item.itemCode);
        }
        return sum;
      }, 0);
      const netBalance = totalProfit - totalExpenses - totalLoss; // Profit - Expenses - Loss
      
      // Calculate final running balance
      let calculatedRunningBalance = 0;
      sortedData.forEach((item) => {
        const profit = parseFloat(item.cr) || 0;
        const expense = parseFloat(item.dr) || 0;
        const loss = item.progID === 'LOSS' ? extractLossAmount(item.itemCode) : 0;
        calculatedRunningBalance += (profit - expense - loss);
      });
      
      setRunningBalance(calculatedRunningBalance);
      
      setSummary({
        totalExpenses,
        totalProfit,
        totalLoss,
        netBalance,
        transactionCount: sortedData.length,
      });
      
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error fetching posting report:", error);
      setStatus({ loading: false, error: true });
      setErrorLogin("Failed to fetch report data");
      setShowToast(true);
    }
  };

  // NEW FUNCTION: Handle transaction deletion
  const handleDeleteTransaction = (transaction) => {
    if (!transaction) return;
    
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  // NEW FUNCTION: Confirm and execute deletion
  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    setDeleteStatus({ loading: true, error: false });
    
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("sn", transactionToDelete.sn);
      formData.append("id", transactionToDelete.id);

      
      // Assuming your API endpoint for deletion
      // You'll need to adjust this endpoint based on your backend
      const response = await axios.post(
        baseUrl() + "ledgers/deleteTransaction", // Adjust this endpoint
        formData,
        config
      );
      
      if (response.data.success) {
        // Remove the transaction from local state
        const updatedPosting = posting.filter(t => t.sn !== transactionToDelete.sn);
        setPosting(updatedPosting);
        
        // Recalculate daily summary
        const dailyGrouped = groupTransactionsByDay(updatedPosting);
        setDailySummary(dailyGrouped);
        
        // Recalculate summary
        const totalExpenses = updatedPosting.reduce((sum, item) => sum + (parseFloat(item.dr) || 0), 0);
        const totalProfit = updatedPosting.reduce((sum, item) => sum + (parseFloat(item.cr) || 0), 0);
        const totalLoss = updatedPosting.reduce((sum, item) => {
          if (item.progID === 'LOSS') {
            return sum + extractLossAmount(item.itemCode);
          }
          return sum;
        }, 0);
        const netBalance = totalProfit - totalExpenses - totalLoss;
        
        setSummary({
          totalExpenses,
          totalProfit,
          totalLoss,
          netBalance,
          transactionCount: updatedPosting.length,
        });
        
        // Show success message
        setErrorLogin("Transaction deleted successfully!");
        setShowToast(true);
        
        // Close dialog
        setDeleteDialogOpen(false);
        setTransactionToDelete(null);
      } else {
        throw new Error(response.data.message || "Failed to delete transaction");
      }
      
      setDeleteStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error deleting transaction:", error);
      setDeleteStatus({ loading: false, error: true });
      setErrorLogin(error.response?.data?.message || "Failed to delete transaction");
      setShowToast(true);
    }
  };

  // NEW FUNCTION: Cancel delete
  const cancelDeleteTransaction = () => {
    setDeleteDialogOpen(false);
    setTransactionToDelete(null);
    setDeleteStatus({ loading: false, error: false });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const handleExport = () => {
    console.log("Exporting data...");
  };

  const handleRefresh = () => {
    const selectedLedger = watch("ledger");
    if (selectedLedger) {
      getPostingReport({ ledger: selectedLedger });
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handlePrintOpen = () => {
    setPrintDialogOpen(true);
  };

  const handlePrintClose = () => {
    setPrintDialogOpen(false);
  };

  // Function to calculate running balance per row for detailed view
  const calculateRunningBalance = (transactions, currentIndex) => {
    let balance = 0;
    for (let i = 0; i <= currentIndex; i++) {
      const profit = parseFloat(transactions[i]?.cr) || 0;
      const expense = parseFloat(transactions[i]?.dr) || 0;
      const loss = transactions[i]?.progID === 'LOSS' ? extractLossAmount(transactions[i]?.itemCode) : 0;
      balance += (profit - expense - loss);
    }
    return balance;
  };

  // Calculate totals for current view
  const getViewTotals = () => {
    if (viewMode === 'detailed') {
      return summary;
    } else {
      // Calculate from daily summary
      const totalExpenses = dailySummary.reduce((sum, day) => sum + day.totalExpenses, 0);
      const totalProfit = dailySummary.reduce((sum, day) => sum + day.totalProfit, 0);
      const totalLoss = dailySummary.reduce((sum, day) => sum + day.totalLoss, 0);
      const netBalance = dailySummary.reduce((sum, day) => sum + day.netBalance, 0);
      const transactionCount = dailySummary.reduce((sum, day) => sum + day.transactionCount, 0);
      
      return {
        totalExpenses,
        totalProfit,
        totalLoss,
        netBalance,
        transactionCount
      };
    }
  };

  const viewTotals = getViewTotals();
  const selectedLedger = watch("ledger");
  const selectedLedgerName = allLedger.find(l => l.sn === selectedLedger)?.name || "";

  // Print content component (keep as is)
  const PrintContent = React.forwardRef(({ posting, viewMode, dailySummary, viewTotals, selectedLedgerName, startDate, endDate }, ref) => {
    // ... (keep existing PrintContent implementation)
    return (
      <div ref={ref} style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        {/* Your existing print content */}
      </div>
    );
  });

  return (
    <IonPage className="pushDown">
                                            <MenuButton /> {/* This adds the hamburger icon */}
      
      <IonContent fullscreen>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight="600" gutterBottom>
              Ledger Report
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View detailed transaction history with Expenses, Profit, Loss, and Balance columns
            </Typography>
          </Box>

          {/* Filter Card */}
          <Card sx={{ mb: 4, borderRadius: 2, boxShadow: theme.shadows[2] }}>
            <CardHeader
              title="Report Filters"
              avatar={<FilterList />}
              action={
                <Stack direction="row" spacing={1}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    <ToggleButton value="detailed" disabled={posting.length === 0}>
                      <Tooltip title="Detailed View">
                        <ViewList />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="summarized" disabled={posting.length === 0}>
                      <Tooltip title="Daily Summary">
                        <ViewDay />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    startIcon={<PrintIcon />}
                    onClick={handlePrintOpen}
                    disabled={posting.length === 0}
                    variant="outlined"
                    size="small"
                    color="info"
                  >
                    Print
                  </Button>
                  <Button
                    startIcon={<Download />}
                    onClick={handleExport}
                    disabled={posting.length === 0}
                    variant="outlined"
                    size="small"
                  >
                    Export
                  </Button>
                  <Button
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={posting.length === 0}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  >
                    Refresh
                  </Button>
                </Stack>
              }
            />
            <Divider />
            <CardContent>
              <form onSubmit={handleSubmit(getPostingReport)}>
                <Grid container spacing={3}>
                  {/* Ledger Selection */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="medium">
                      <InputLabel id="ledger-select-label">Select Ledger</InputLabel>
                      <Controller
                        name="ledger"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="ledger-select-label"
                            label="Select Ledger"
                            error={!!errors.ledger}
                            disabled={status.loading}
                            startAdornment={<AccountBalance sx={{ mr: 1 }} />}
                          >
                            <MenuItem value="">
                              <em>Select a ledger...</em>
                            </MenuItem>
                            {allLedger.map((ledger, index) => (
                              <MenuItem key={index} value={ledger.sn}>
                                {ledger.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.ledger && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                          {errors.ledger.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Date Range */}
                  <Grid item xs={12} md={5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            maxDate={endDate}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "medium",
                                InputProps: {
                                  startAdornment: <CalendarMonth sx={{ mr: 1, color: 'action.active' }} />,
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            minDate={startDate}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "medium",
                                InputProps: {
                                  startAdornment: <CalendarMonth sx={{ mr: 1, color: 'action.active' }} />,
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </LocalizationProvider>
                  </Grid>

                  {/* Generate Button */}
                  <Grid item xs={12} md={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={status.loading}
                      startIcon={<Search />}
                      sx={{
                        height: '56px',
                        borderRadius: 1,
                        fontWeight: 600,
                      }}
                    >
                      Generate Report
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          {/* Summary Cards - Updated for 4 columns */}
          {posting.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* ... (keep existing summary cards) */}
            </Grid>
          )}

          {/* Transactions Table - UPDATED with Delete Button */}
          <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
            <CardHeader
              title={
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {viewMode === 'detailed' ? 'Transaction History' : 'Daily Summary'}
                  </Typography>
                  <Chip 
                    label={`${viewMode === 'detailed' ? 'Detailed' : 'Summarized'} View`} 
                    color="primary"
                    size="small"
                    icon={viewMode === 'detailed' ? <ViewList /> : <ViewDay />}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Balance = Profit - Expenses - Loss
                  </Typography>
                </Stack>
              }
              subheader={
                viewMode === 'detailed' 
                  ? `Showing ${posting.length} individual transactions`
                  : `Showing ${dailySummary.length} daily summaries (${summary.transactionCount} total transactions)`
              }
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton 
                    color="info" 
                    onClick={handlePrintOpen}
                    disabled={posting.length === 0}
                    size="small"
                  >
                    <Tooltip title="Print Report">
                      <PrintIcon />
                    </Tooltip>
                  </IconButton>
                  <IconButton 
                    color="secondary" 
                    onClick={handleRefresh}
                    disabled={posting.length === 0}
                    size="small"
                  >
                    <Tooltip title="Refresh Data">
                      <Refresh />
                    </Tooltip>
                  </IconButton>
                  <Typography variant="caption" color="text.secondary">
                    View:
                  </Typography>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="detailed">
                      <Tooltip title="Detailed View">
                        <ViewList />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="summarized">
                      <Tooltip title="Daily Summary">
                        <ViewDay />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {posting.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                        {viewMode === 'detailed' ? (
                          <>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Expenses (Dr)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Profit (Cr)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Loss (ItemCode)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Balance (Cr-Dr-Loss)</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell> {/* NEW COLUMN */}
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Day</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Transactions</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Total Expenses</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Total Profit</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Total Loss</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Daily Net</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Cumulative Balance</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell> {/* NEW COLUMN */}
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewMode === 'detailed' ? (
                        // Detailed View - UPDATED with Delete Button
                        posting.map((transaction, index) => {
                          const profit = parseFloat(transaction.cr) || 0;
                          const expense = parseFloat(transaction.dr) || 0;
                          const loss = transaction.progID === 'LOSS' ? extractLossAmount(transaction.itemCode) : 0;
                          const transactionBalance = profit - expense - loss;
                          const cumulativeBalance = calculateRunningBalance(posting, index);
                          
                          return (
                            <TableRow 
                              key={index}
                              sx={{ 
                                '&:hover': { 
                                  bgcolor: theme.palette.action.hover 
                                },
                                borderBottom: index < posting.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                              }}
                            >
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2">
                                  {formatDate(transaction.dateCreated)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(transaction.dateCreated).format('HH:mm')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2">
                                  {transaction.desp}
                                </Typography>
                                {transaction.progID === 'LOSS' && (
                                  <Typography variant="caption" color="warning.main" fontWeight="bold">
                                    LOSS TRANSACTION
                                  </Typography>
                                )}
                                {transaction.reference && (
                                  <Typography variant="caption" color="text.secondary">
                                    Ref: {transaction.reference}
                                  </Typography>
                                )}
                                {transaction.sn && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    ID: {transaction.sn}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {expense > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="error.main"
                                  >
                                    {formatCurrency(expense)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {profit > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="success.main"
                                  >
                                    {formatCurrency(profit)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {loss > 0 && (
                                  <>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="500"
                                      color="warning.main"
                                    >
                                      {formatCurrency(loss)}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      color="warning.main"
                                      sx={{ display: 'block' }}
                                    >
                                      ItemCode: {transaction.itemCode}
                                    </Typography>
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={cumulativeBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(cumulativeBalance)}
                                </Typography>
                                {transactionBalance !== 0 && (
                                  <Typography 
                                    variant="caption" 
                                    color={transactionBalance > 0 ? 'success.main' : 'error.main'}
                                    sx={{ display: 'block' }}
                                  >
                                    {transactionBalance > 0 ? '+' : ''}{formatCurrency(transactionBalance)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Tooltip title="Delete Transaction">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteTransaction(transaction)}
                                    disabled={deleteStatus.loading}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        // Summarized View (Daily) - UPDATED with Delete Buttons for each transaction in day
                        dailySummary.map((day, index) => (
                          <React.Fragment key={index}>
                            <TableRow 
                              sx={{ 
                                '&:hover': { 
                                  bgcolor: theme.palette.action.hover 
                                },
                                borderBottom: index < dailySummary.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                              }}
                            >
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" fontWeight="500">
                                  {day.formattedDate}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {day.dayOfWeek}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Chip 
                                  label={`${day.transactionCount} trans`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {day.transactionCount === 1 ? '1 transaction' : `${day.transactionCount} transactions`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {day.totalExpenses > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="error.main"
                                  >
                                    {formatCurrency(day.totalExpenses)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {day.totalProfit > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="success.main"
                                  >
                                    {formatCurrency(day.totalProfit)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                {day.totalLoss > 0 && (
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="500"
                                    color="warning.main"
                                  >
                                    {formatCurrency(day.totalLoss)}
                                    </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={day.netBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(day.netBalance)}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color={day.netBalance >= 0 ? 'success.main' : 'error.main'}
                                  sx={{ display: 'block' }}
                                >
                                  {day.netBalance > 0 ? '+' : ''}{formatCurrency(day.netBalance)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={day.runningBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(day.runningBalance)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Tooltip title="View day transactions">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => {
                                      // Option to switch to detailed view for this day
                                      setViewMode('detailed');
                                      // You could filter to show only this day's transactions
                                    }}
                                  >
                                    <ViewList fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            {/* Optional: Show individual transactions under each day in summarized view */}
                            {day.transactions && day.transactions.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={10} sx={{ py: 1, bgcolor: theme.palette.grey[50] }}>
                                  <Typography variant="caption" fontWeight="500">
                                    Individual Transactions:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                    {day.transactions.map((trans, transIndex) => (
                                      <Chip
                                        key={transIndex}
                                        label={`${trans.desp}: ${formatCurrency(parseFloat(trans.cr) || parseFloat(trans.dr) || 0)}`}
                                        size="small"
                                        variant="outlined"
                                        onDelete={() => handleDeleteTransaction(trans)}
                                        deleteIcon={<DeleteIcon fontSize="small" />}
                                        color="default"
                                      />
                                    ))}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))
                      )}
                      
                      {/* Footer Row with Totals */}
                      {posting.length > 0 && (
                        <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                          {viewMode === 'detailed' ? (
                            <>
                              <TableCell colSpan={3} align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600">
                                  Totals:
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {viewMode === 'summarized' ? `${dailySummary.length} days` : `${summary.transactionCount} transactions`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="error.main">
                                  {formatCurrency(viewTotals.totalExpenses)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="success.main">
                                  {formatCurrency(viewTotals.totalProfit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600" color="warning.main">
                                  {formatCurrency(viewTotals.totalLoss)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography 
                                  variant="body1" 
                                  fontWeight="600"
                                  color={viewTotals.netBalance >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(viewTotals.netBalance)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Profit - Expenses - Loss
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}></TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell colSpan={4} align="right" sx={{ py: 2 }}>
                                <Typography variant="body1" fontWeight="600">
                                  Period Totals:
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {dailySummary.length} days
                                </Typography>
                              </TableCell>
                              {/* ... (rest of totals row) */}
                              <TableCell sx={{ py: 2 }}></TableCell>
                            </>
                          )}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <DateRange sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Transactions Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {watch("ledger") 
                      ? "No transactions found for the selected ledger and date range" 
                      : "Select a ledger and date range to view transactions"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Info Alert */}
          {posting.length > 0 && (
            <Alert 
              severity="info" 
              sx={{ mt: 3, borderRadius: 2 }}
              icon={<Info />}
            >
              <Typography variant="body2">
                {viewMode === 'detailed' 
                  ? `Showing ${posting.length} individual transactions from ${formatDate(startDate)} to ${formatDate(endDate)}.`
                  : `Showing ${dailySummary.length} daily summaries aggregating ${summary.transactionCount} total transactions from ${formatDate(startDate)} to ${formatDate(endDate)}.`}
                {" "}Balance calculation: Profit (Cr) - Expenses (Dr) - Loss
                <br />
                <strong>View Mode:</strong> {viewMode === 'detailed' ? 'Detailed transaction list' : 'Daily aggregated summary'}
                <br />
                <strong>Delete Function:</strong> Click the delete icon to remove individual transactions
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Print Dialog (keep as is) */}
        <Dialog open={printDialogOpen} onClose={handlePrintClose} maxWidth="lg" fullWidth>
          {/* ... (existing print dialog) */}
        </Dialog>

        {/* NEW: Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={cancelDeleteTransaction}>
          <DialogTitle>Delete Transaction</DialogTitle>
          <DialogContent>
            {transactionToDelete && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Are you sure you want to delete this transaction?
                  </Typography>
                  <Typography variant="caption">
                    This action cannot be undone.
                  </Typography>
                </Alert>
                
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><Typography variant="body2" fontWeight="500">Date:</Typography></TableCell>
                        <TableCell><Typography variant="body2">{formatDate(transactionToDelete.dateCreated)}</Typography></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Typography variant="body2" fontWeight="500">Description:</Typography></TableCell>
                        <TableCell><Typography variant="body2">{transactionToDelete.desp}</Typography></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Typography variant="body2" fontWeight="500">Reference:</Typography></TableCell>
                        <TableCell><Typography variant="body2">{transactionToDelete.reference || 'N/A'}</Typography></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Typography variant="body2" fontWeight="500">Expense (Dr):</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error.main">
                            {transactionToDelete.dr ? formatCurrency(parseFloat(transactionToDelete.dr)) : '0.00'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Typography variant="body2" fontWeight="500">Profit (Cr):</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            {transactionToDelete.cr ? formatCurrency(parseFloat(transactionToDelete.cr)) : '0.00'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {transactionToDelete.progID === 'LOSS' && (
                        <TableRow>
                          <TableCell><Typography variant="body2" fontWeight="500">Loss Amount:</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" color="warning.main">
                              {extractLossAmount(transactionToDelete.itemCode) ? formatCurrency(extractLossAmount(transactionToDelete.itemCode)) : '0.00'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell><Typography variant="body2" fontWeight="500">Transaction ID:</Typography></TableCell>
                        <TableCell><Typography variant="body2">{transactionToDelete.sn}</Typography></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDeleteTransaction} disabled={deleteStatus.loading}>
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteTransaction} 
              color="error" 
              variant="contained"
              disabled={deleteStatus.loading}
              startIcon={deleteStatus.loading ? null : <DeleteIcon />}
            >
              {deleteStatus.loading ? 'Deleting...' : 'Delete Transaction'}
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading
          message="Loading ledger report..."
          spinner="circles"
          isOpen={status.loading}
        />
        <IonToast 
          position="middle" 
          isOpen={showToast} 
          message={errorLogin} 
          duration={3000}
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default LedgerReport_sales_only_admin_view;